import React, { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import {
  API_KEY_ACCESS_GROUPS,
  FIREBASE_ONLY_ENDPOINTS,
  PUBLIC_API_ENDPOINTS,
  type ApiEndpointDoc,
  type ApiHttpMethod,
} from "@/data/api-endpoints";

function methodClasses(method: ApiHttpMethod): string {
  switch (method) {
    case "GET":
      return "border-brand/30 bg-brand/10 text-brand";
    case "POST":
      return "border-emerald-400/30 bg-emerald-400/10 text-emerald-300";
    case "PUT":
      return "border-amber-400/30 bg-amber-400/10 text-amber-300";
    case "PATCH":
      return "border-violet-400/30 bg-violet-400/10 text-violet-300";
    case "DELETE":
      return "border-danger/30 bg-danger/10 text-danger";
    default:
      return "border-white/15 bg-surface/10 text-white/60";
  }
}

function EndpointRow({ endpoint }: { endpoint: ApiEndpointDoc }) {
  return (
    <div className="rounded-[14px] border border-white/10 bg-surface/20 px-3 py-3">
      <div className="flex flex-wrap items-start gap-2">
        <span
          className={`shrink-0 rounded-md border px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wide ${methodClasses(endpoint.method)}`}
        >
          {endpoint.method}
        </span>
        <code className="min-w-0 flex-1 break-all font-mono text-xs text-white/85">
          {endpoint.path}
        </code>
      </div>
      <p className="mt-2 text-sm text-white/60">{endpoint.description}</p>
      {endpoint.note ? (
        <p className="mt-1 text-xs text-white/45">{endpoint.note}</p>
      ) : null}
    </div>
  );
}

function EndpointGroup({
  title,
  description,
  endpoints,
  defaultOpen = true,
}: {
  title: string;
  description?: string;
  endpoints: ApiEndpointDoc[];
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="rounded-[16px] border border-white/10 bg-surface/10">
      <button
        type="button"
        onClick={() => setIsOpen((open) => !open)}
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left"
        aria-expanded={isOpen}
      >
        <span>
          <span className="block text-sm font-semibold text-white">{title}</span>
          {description ? (
            <span className="mt-0.5 block text-xs text-white/45">{description}</span>
          ) : null}
          <span className="mt-1 block text-[11px] text-white/35">
            {endpoints.length} endpoint{endpoints.length === 1 ? "" : "s"}
          </span>
        </span>
        <ChevronDown
          className={`mt-0.5 h-4 w-4 shrink-0 text-white/45 transition-transform ${isOpen ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>

      {isOpen ? (
        <div className="space-y-2 border-t border-white/10 px-4 py-3">
          {endpoints.map((endpoint) => (
            <EndpointRow
              key={`${endpoint.method}-${endpoint.path}`}
              endpoint={endpoint}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

export interface ApiEndpointReferenceProps {
  apiBaseUrl: string;
}

export const ApiEndpointReference: React.FC<ApiEndpointReferenceProps> = ({
  apiBaseUrl,
}) => {
  const endpointCount = useMemo(
    () =>
      API_KEY_ACCESS_GROUPS.reduce(
        (total, group) => total + group.endpoints.length,
        0,
      ),
    [],
  );

  return (
    <SettingsSectionCard
      title="API reference"
      description="Endpoints available when authenticating with an API key. Keys act as you; scope and read-only limits are optional when creating a key."
    >
      <div className="space-y-5">
        <div className="rounded-[14px] border border-white/10 bg-surface/20 px-4 py-3 text-sm text-white/60">
          <p>
            Send your key in the{" "}
            <code className="rounded bg-surface px-1 py-0.5 font-mono text-xs text-white/80">
              Authorization
            </code>{" "}
            header:
          </p>
          <code className="mt-2 block overflow-x-auto rounded-[10px] bg-surface px-3 py-2 font-mono text-xs text-white/85">
            Authorization: Bearer bc_&lt;keyId&gt;_&lt;secret&gt;
          </code>
          <p className="mt-3 text-xs text-white/45">
            Base URL:{" "}
            <span className="font-mono text-white/65">{apiBaseUrl}</span>
          </p>
          <p className="mt-2 text-xs text-white/45">
            {endpointCount} endpoints accept API keys. Replace{" "}
            <code className="font-mono">:id</code> with a startup ID from{" "}
            <code className="font-mono">GET /startups</code>. Keys without a scope can access all startups you can reach; scoped keys only see listed startups. Read-only keys can use GET endpoints only.
          </p>
        </div>

        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-white/45">
            Available with API key
          </h4>
          {API_KEY_ACCESS_GROUPS.map((group, index) => (
            <EndpointGroup
              key={group.title}
              title={group.title}
              description={group.description}
              endpoints={group.endpoints}
              defaultOpen={index < 3}
            />
          ))}
        </div>

        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-white/45">
            Firebase sign-in only
          </h4>
          <p className="text-xs text-white/45">
            These require a Firebase ID token from the web app, not an API key.
          </p>
          <div className="space-y-2">
            {FIREBASE_ONLY_ENDPOINTS.map((endpoint) => (
              <EndpointRow
                key={`${endpoint.method}-${endpoint.path}`}
                endpoint={endpoint}
              />
            ))}
          </div>
        </div>

        <div className="space-y-3">
          <h4 className="text-xs font-semibold uppercase tracking-wide text-white/45">
            Public
          </h4>
          <div className="space-y-2">
            {PUBLIC_API_ENDPOINTS.map((endpoint) => (
              <EndpointRow
                key={`${endpoint.method}-${endpoint.path}`}
                endpoint={endpoint}
              />
            ))}
          </div>
        </div>
      </div>
    </SettingsSectionCard>
  );
};
